// NIGHT-DIRECTSTAFF-2026-10-29 (the hub's 2026-11-02 addendum): "Create candidate" in one tap on a lead, through the same database
// function the agents call -- public.candidate_from_lead(p_lead_id, p_by) (migrations/2026-11-02-candidate-from-lead.sql, filed for
// the hub). It returns the candidate id: the existing one when a candidate already has the lead's phone or email (the oldest,
// never a merged one -- the "Possible duplicate" rule), else a new one shaped like Promote's; the database logs it. Never a duplicate.
// Before the hub applies the file the probe says no and the lead keeps its Promote button (the form), as today.
import { CANDIDATE_FORM_TYPES } from './leadClassification';
import { leadKind } from './leadFilters';

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const ERRORS = {
  'candidate:not_a_candidate_lead': 'That lead is not a candidate lead (a facility, or a form that is not an application). Nothing was made.',
  'candidate:lead_hidden': 'That lead is marked spam, test or merged. Nothing was made.',
  'candidate:no_contact': 'That lead has no phone number or email to match on. Use Promote to enter them by hand.',
  'candidate:no_such_lead': 'That lead is no longer there. Refresh.',
};
export const cflError = (e) => { const m = /candidate:[a-z_]+|onboarding:[a-z_]+/.exec(String((e && e.message) || '')); return (m && ERRORS[m[0]]) || (e && e.message) || 'The candidate could not be made.'; };
const national = (v) => { const d = String(v || '').replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d.length >= 10 ? d.slice(-10) : ''; };

/** The database's rule, so the button shows only where it will work: a whitelisted form, not a facility, not spam / test /
 *  merged, with a phone (10 digits) or an email. */
export function canCreateFromLead(l) {
  if (!l) return false;
  if (!CANDIDATE_FORM_TYPES.has(s(l.form_type).toLowerCase()) || leadKind(l) === 'facility' || s(l.form_type).toLowerCase() === 'employer_inquiry') return false;
  if (l.spam_marked_at || l.test_marked_at || l.merged_into) return false;
  return !!national(l.phone) || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s(l.email));
}

/** DAY-DIRECTSTAFF-2026-11-01 §1: the candidate already carries this lead's link line ("... lead_id <uuid> ..."; Promote, the agents, or
 *  Create / Link here) */
export const leadLinked = (candidate, leadId) => !!candidate && !!leadId && String(candidate.notes || '').includes(`lead_id ${leadId}`);

// DAY-DIRECTSTAFF-2026-11-09 §1 (QA R3 · G, "blocks a lead"): job applicants the agents cannot send a profile link to, because no
// CRM candidate has their phone or email ("Blocked -- no CRM candidate" in the brief). Today lists them with Create candidate.
const APPLICANT_DAYS = 30;
const digits10 = (v) => national(v);
/** leads (job_apply, the last 30 days, could be made a candidate, not linked) with no candidate on the same phone or email -> [{ lead, days }], oldest first */
export function applicantsWithoutCandidate({ leads = [], candidates = [], now = Date.now(), days = APPLICANT_DAYS }) {
  const phones = new Set(); const emails = new Set();
  for (const c of candidates || []) { const p = digits10(c.phone); if (p) phones.add(p); const e = s(c.email).toLowerCase(); if (e) emails.add(e); }
  const out = [];
  for (const l of leads || []) {
    if (s(l.form_type).toLowerCase() !== 'job_apply' || !canCreateFromLead(l)) continue;
    const at = Date.parse(l.created_at || ''); if (Number.isNaN(at) || now - at > days * 864e5) continue;
    const p = national(l.phone); const e = s(l.email).toLowerCase();
    if ((p && phones.has(p)) || (e && emails.has(e)) || (candidates || []).some((c) => leadLinked(c, l.id))) continue;
    out.push({ lead: l, days: Math.max(0, Math.floor((now - at) / 864e5)) });
  }
  return out.sort((a, b) => s(a.lead.created_at).localeCompare(s(b.lead.created_at)));
}

export function makeCandidateFromLeadApi(supabase) {
  return {
    /** the hub applied it? A null lead id runs nothing: staff get candidate:no_such_lead from the function, absent = PGRST202 */
    async probe() {
      try {
        const { error } = await supabase.rpc('candidate_from_lead', { p_lead_id: null, p_by: null });
        return !!error && /candidate:no_such_lead/.test(error.message || '');
      } catch { return false; }
    },
    async create(leadId) {
      const { data, error } = await supabase.rpc('candidate_from_lead', { p_lead_id: leadId, p_by: null });
      if (error) throw new Error(cflError(error));
      return Number(data);
    },
  };
}
